import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  Chip,
  OutlinedInput,
  InputLabel,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { Close as CloseIcon, Build as BuildIcon, Add as AddIcon } from '@mui/icons-material';
import { createSystemTool, SystemTool } from 'src/api2/toolsApi';
import { useSnackbar } from 'src/view/components/SnackbarService';

interface AddToolModalProps {
  open: boolean;
  onClose: () => void;
  onToolAdded: (newTool: SystemTool) => void;
}

const AVAILABLE_MODULES = ['carbon', 'biodiversity', 'environmental', 'climate'];

const AddToolModal: React.FC<AddToolModalProps> = ({ open, onClose, onToolAdded }) => {
  const { showSnackbar, snackbarType } = useSnackbar();

  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>(['biodiversity']);
  const [customModuleInput, setCustomModuleInput] = useState('');
  const [trialLimit, setTrialLimit] = useState<string>('');
  const [isActive, setIsActive] = useState(true);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resetForm = () => {
    setName('');
    setSlug('');
    setDescription('');
    setSelectedModules(['biodiversity']);
    setCustomModuleInput('');
    setTrialLimit('');
    setIsActive(true);
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleModuleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    setSelectedModules(typeof value === 'string' ? value.split(',') : value);
  };

  const handleAddCustomModule = () => {
    const trimmed = customModuleInput.trim().toLowerCase().replace(/[^a-z0-9_-]/g, '');
    if (trimmed && !selectedModules.includes(trimmed)) {
      setSelectedModules((prev) => [...prev, trimmed]);
      setCustomModuleInput('');
    }
  };

  const handleRemoveModule = (modToRemove: string) => {
    setSelectedModules((prev) => prev.filter((m) => m !== modToRemove));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      setError('Tool Name is required.');
      return;
    }

    const generatedSlug = slug.trim() || name.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
    const parsedTrialLimit = trialLimit.trim() ? parseInt(trialLimit.trim(), 10) : undefined;

    setSubmitting(true);
    setError(null);

    try {
      const created = await createSystemTool({
        name: name.trim(),
        slug: generatedSlug,
        description: description.trim() || undefined,
        module: selectedModules.length > 0 ? selectedModules : ['general'],
        is_active: isActive,
        trial_limit: parsedTrialLimit !== undefined && !isNaN(parsedTrialLimit) ? parsedTrialLimit : null,
      });

      showSnackbar({ message: 'Tool created successfully', type: snackbarType.SUCCESS });
      onToolAdded(created);
      handleClose();
    } catch (err: any) {
      const msg = err?.detail?.[0]?.msg || err?.detail || err?.message || 'Failed to create tool in database';
      setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
      showSnackbar({ message: `Error creating tool: ${msg}`, type: snackbarType.ERROR });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#111111',
          border: '1px solid #222222',
          borderRadius: '16px',
          color: '#FFFFFF',
          p: 1,
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ p: 1, borderRadius: '10px', bgcolor: 'rgba(16, 185, 129, 0.1)', color: '#10B981', display: 'flex' }}>
            <BuildIcon sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.1rem' }}>
              Add New Tool
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
              Register a new tool matching the database schema (`tools` table)
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={handleClose} size="small" sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#FFF' } }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, py: 2 }}>
          {error && <Alert severity="error" sx={{ bgcolor: 'rgba(239, 68, 68, 0.1)', color: '#EF4444', border: '1px solid rgba(239, 68, 68, 0.2)' }}>{error}</Alert>}

          <TextField
            label="Tool Name (name) *"
            placeholder="e.g. Nature Atlas, Bioacoustics AI"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            required
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#181818',
                color: '#FFF',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#555' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#888' },
            }}
          />

          <TextField
            label="Tool Slug (slug) *"
            placeholder="e.g. nature_atlas, bioacoustic"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Unique database slug identifier (autogenerated from name if empty)"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#181818',
                color: '#FFF',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#555' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#888' },
              '& .MuiFormHelperText-root': { color: '#666' },
            }}
          />

          {/* Module (ARRAY of Strings) */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <FormControl size="small" fullWidth sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#181818',
                color: '#FFF',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#555' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#888' },
            }}>
              <InputLabel id="modules-label">Modules (module) *</InputLabel>
              <Select
                labelId="modules-label"
                multiple
                value={selectedModules}
                onChange={handleModuleSelectChange}
                input={<OutlinedInput label="Modules (module) *" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((val) => (
                      <Chip key={val} label={val} size="small" sx={{ bgcolor: 'rgba(16,185,129,0.2)', color: '#10B981', border: '1px solid #10B981' }} />
                    ))}
                  </Box>
                )}
              >
                {AVAILABLE_MODULES.map((mod) => (
                  <MenuItem key={mod} value={mod}>
                    {mod}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                placeholder="Add custom module tag..."
                size="small"
                value={customModuleInput}
                onChange={(e) => setCustomModuleInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddCustomModule();
                  }
                }}
                sx={{
                  flexGrow: 1,
                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#141414',
                    color: '#FFF',
                    fontSize: '12px',
                    '& fieldset': { borderColor: '#252525' },
                  },
                }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={handleAddCustomModule}
                startIcon={<AddIcon />}
                sx={{ color: '#10B981', borderColor: '#10B981', fontSize: '11px', textTransform: 'none' }}
              >
                Add Module
              </Button>
            </Box>

            {/* Display Selected Module Tags */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, pt: 0.5 }}>
              {selectedModules.map((mod) => (
                <Chip
                  key={mod}
                  label={mod}
                  size="small"
                  onDelete={() => handleRemoveModule(mod)}
                  sx={{
                    bgcolor: 'rgba(16, 185, 129, 0.15)',
                    color: '#10B981',
                    border: '1px solid rgba(16, 185, 129, 0.3)',
                    fontWeight: 700,
                    fontSize: '11px',
                    '& .MuiChip-deleteIcon': { color: '#10B981', '&:hover': { color: '#EF4444' } },
                  }}
                />
              ))}
            </Box>
          </Box>

          <TextField
            label="Trial Usage Limit (trial_limit)"
            placeholder="e.g. 10 (Leave blank for unlimited)"
            type="number"
            value={trialLimit}
            onChange={(e) => setTrialLimit(e.target.value)}
            fullWidth
            variant="outlined"
            size="small"
            helperText="Optional max trial uses granted per user before requiring active tool access"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#181818',
                color: '#FFF',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#555' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#888' },
              '& .MuiFormHelperText-root': { color: '#666' },
            }}
          />

          <TextField
            label="Description (description)"
            placeholder="Describe what this tool does..."
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#181818',
                color: '#FFF',
                '& fieldset': { borderColor: '#333' },
                '&:hover fieldset': { borderColor: '#555' },
                '&.Mui-focused fieldset': { borderColor: '#10B981' },
              },
              '& .MuiInputLabel-root': { color: '#888' },
            }}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, bgcolor: '#181818', borderRadius: '10px', border: '1px solid #282828' }}>
            <Box>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: '#FFF' }}>Status (is_active)</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#888' }}>Tool is active and available for organisation provisioning</Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
                  }}
                />
              }
              label={isActive ? 'Active' : 'Inactive'}
              sx={{ color: isActive ? '#10B981' : '#888', fontWeight: 700 }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={handleClose} disabled={submitting} sx={{ color: '#888', '&:hover': { color: '#FFF' } }}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={submitting}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} sx={{ color: '#FFF' }} /> : <BuildIcon />}
            sx={{
              bgcolor: '#10B981',
              color: '#000',
              fontWeight: 800,
              px: 3,
              borderRadius: '8px',
              '&:hover': { bgcolor: '#059669' },
            }}
          >
            {submitting ? 'Creating...' : 'Register Tool'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AddToolModal;
